import { createGmSocket } from "./connection";
import { queueEvent } from "../analytics";
import { EVENTS } from "../analytics/events";

/**
 * Game-master bridge for the WEB game (mirror of the app's
 * src/services/liveBridge.js). Module singleton so the sibling routes
 * (Game board, Question, Winning) all emit through it; the session survives
 * page refreshes via localStorage (code + gmKey + QR) and gm:resume.
 */

const EMIT_DEBOUNCE_MS = 250;
const STORAGE_KEY = "liveGmSession";

const initial = () => ({
  active: false,
  code: null,
  url: null,
  qrDataUrl: null,
  presence: { total: 0, byTeam: {} },
  votes: null, // { voteEpoch, group, counts, total }
});

let state = initial();
let snapshot = { ...state };
let gmKey = null;
let gameCode = null; // the web game's own code, to key the stored session
let lastState = null;
let pendingPatch = null;
let emitTimer = null;
let seq = 1;
let socket = null;
const listeners = new Set();

const notify = () => {
  snapshot = { ...state };
  listeners.forEach((fn) => fn());
};

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const getSnapshot = () => snapshot;

export const isActive = () => state.active;

/* ───────── storage ───────── */

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStored = (value) => {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — session just won't survive refresh */
  }
};

/* ───────── serialization (web game shape → neutral live doc) ───────── */

const teamId = (t) => String(t?._id ?? t?.id ?? t?.name);
export { teamId };

const serializeTeams = (game) =>
  (game?.teams || []).map((t) => ({
    id: teamId(t),
    name: t.name || "فريق",
    points: t.points || 0,
    image: t.image?.image || t.image || null,
  }));

const innerQ = (w) => w?.question || w?.customQuestion;

export const powerUpsFromGame = (game) => {
  const perTeam = {};
  (game?.teams || []).forEach((t) => {
    const used = {};
    (t.powerUps || []).forEach((pu) => {
      used[pu.name] = !!pu.used;
    });
    perTeam[teamId(t)] = used;
  });
  return {
    perTeam,
    defs: [
      { name: "one", label: "ما عجبني بغيّره", icon: "swap" },
      { name: "two", label: "اتصال بصديق", icon: "phone" },
      { name: "three", label: "خيارات متعددة", icon: "choices" },
    ],
    active: {},
  };
};

export const buildBoardState = (game) => ({
  screen: "board",
  meta: {
    gameName: game?.name || "",
    lowToHighPoints: !!game?.lowToHighPoints,
    fadedQuestions: !!localStorage.getItem(`faded_${game?.codeInfo?.code}`),
    // This host labels hidden-points tiles 1..N in board order; spectators
    // show the same labels only when told so (older hosts show blank tiles).
    numberedTiles: true,
  },
  board: {
    modes: (game?.modes || []).map((m) => ({
      id: String(m._id),
      name: m.name,
      image: m.image,
      isCustom: !!m.custom,
      questions: (m.questions || []).map((w) => ({
        id: String(w._id),
        points: innerQ(w)?.points,
      })),
    })),
  },
  teams: serializeTeams(game),
  turnTeamId: teamId((game?.teams || []).find((t) => t.currentTurn) || {}),
  answeredIds: (game?.modes || []).flatMap((m) =>
    (m.questions || [])
      .filter((w) => innerQ(w)?.answered)
      .map((w) => String(w._id))
  ),
  question: null,
  answer: null,
  over: null,
  powerUps: powerUpsFromGame(game),
});

export const buildQuestionState = ({
  game,
  question,
  modeName,
  showAnswer,
  showWhoAnswered,
}) => ({
  screen: showWhoAnswered ? "whoAnswered" : showAnswer ? "answer" : "question",
  teams: serializeTeams(game),
  turnTeamId: teamId((game?.teams || []).find((t) => t.currentTurn) || {}),
  question: {
    id: String(question?._id),
    modeName: modeName || "",
    points: question?.points,
    text: question?.question || "",
    type: question?.type || "",
    isCustom: !!question?.custom,
    questionImage: question?.questionImage || null,
    questionVideo: question?.questionVideo || null,
    questionAudio: question?.questionAudio || null,
    marginText: question?.marginText || null,
  },
  // The answer ships only at reveal so viewers can't scrape it early.
  answer: showAnswer
    ? {
        text: question?.answer || "",
        answerImage: question?.answerImage || null,
        answerVideo: question?.answerVideo || null,
        answerAudio: question?.answerAudio || null,
      }
    : null,
  powerUps: { ...powerUpsFromGame(game), active: lastState?.powerUps?.active || {} },
});

/* ───────── socket plumbing ───────── */

const ensureSocket = () => {
  if (socket) return socket;
  socket = createGmSocket();

  socket.on("live:votes", (payload) => {
    if (!state.active || payload?.code !== state.code) return;
    state.votes = {
      voteEpoch: payload.voteEpoch,
      group: payload.group,
      counts: payload.counts || {},
      total: payload.total || 0,
    };
    notify();
  });

  socket.on("live:presence", (payload) => {
    if (!state.active || payload?.code !== state.code) return;
    state.presence = {
      total: payload.total || 0,
      byTeam: payload.byTeam || {},
    };
    notify();
  });

  socket.on("live:ended", (payload) => {
    if (!state.active || payload?.code !== state.code) return;
    reset();
  });

  socket.on("live:error", (payload) => {
    if (!state.active || payload?.code !== state.code) return;
    if (payload.reason === "session_expired" || payload.reason === "not_owner")
      reset();
  });

  // Reconnect (network blip, server restart): re-claim + full re-emit.
  socket.on("connect", () => {
    if (!state.active || !state.code) return;
    socket.emit("gm:resume", { code: state.code, gmKey }, (res) => {
      if (!res?.ok) return reset();
      if (lastState) {
        seq += 1;
        socket.emit("gm:state", {
          code: state.code,
          gmKey,
          seq,
          patch: { ...lastState },
        });
      }
    });
  });

  return socket;
};

/* ───────── GM actions ───────── */

export const startLive = (webGameCode, initialState) => {
  const s = ensureSocket();
  gameCode = webGameCode || null;

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("timeout")), 15000);
    s.emit("gm:create", { gameId: webGameCode, initialState }, (res) => {
      clearTimeout(timeout);
      if (!res?.ok) return reject(new Error(res?.reason || "server_error"));
      seq = 1;
      gmKey = res.gmKey;
      lastState = { ...initialState };
      pendingPatch = null;
      state = {
        ...initial(),
        active: true,
        code: res.code,
        url: res.url,
        qrDataUrl: res.qrDataUrl,
      };
      writeStored({
        code: res.code,
        gmKey,
        url: res.url,
        qrDataUrl: res.qrDataUrl,
        gameCode,
      });
      notify();
      queueEvent(EVENTS.LIVE_SESSION_STARTED, { gameId: webGameCode });
      resolve(res);
    });
  });
};

/**
 * After a full page refresh: if a stored session matches this web game,
 * silently re-claim it and re-emit the given state.
 */
export const resumeFromStorage = (webGameCode, freshState) => {
  if (state.active) return;
  const stored = readStored();
  if (!stored || stored.gameCode !== webGameCode) return;

  const s = ensureSocket();
  gameCode = webGameCode;
  s.emit("gm:resume", { code: stored.code, gmKey: stored.gmKey }, (res) => {
    if (!res?.ok) {
      writeStored(null);
      return;
    }
    gmKey = stored.gmKey;
    seq = 1;
    lastState = null;
    state = {
      ...initial(),
      active: true,
      code: stored.code,
      url: stored.url,
      qrDataUrl: stored.qrDataUrl,
    };
    if (res.votes) state.votes = res.votes;
    if (res.presence) state.presence = res.presence;
    notify();
    if (freshState) {
      lastState = { ...freshState };
      seq += 1;
      s.emit("gm:state", {
        code: stored.code,
        gmKey,
        seq,
        patch: { ...freshState },
      });
    }
  });
};

export const emitState = (partial = {}) => {
  if (!state.active) return;
  lastState = { ...(lastState || {}), ...partial };
  pendingPatch = { ...(pendingPatch || {}), ...partial };
  if (emitTimer) return;
  emitTimer = setTimeout(() => {
    emitTimer = null;
    if (!state.active || !pendingPatch) return;
    seq += 1;
    socket?.emit("gm:state", {
      code: state.code,
      gmKey,
      seq,
      patch: pendingPatch,
    });
    pendingPatch = null;
  }, EMIT_DEBOUNCE_MS);
};

/** Merge a powerup "active" overlay patch (choices / first letter / pattern / phone). */
export const emitActivePowerUps = (active) => {
  if (!state.active) return;
  emitState({
    powerUps: { ...(lastState?.powerUps || {}), active: active || {} },
  });
};

export const stopLive = (reason = "ended") => {
  if (!state.active) return;
  const code = state.code;
  const key = gmKey;
  queueEvent(EVENTS.LIVE_SESSION_ENDED, { reason });
  reset();
  socket?.emit("gm:end", { code, gmKey: key, reason });
};

const reset = () => {
  clearTimeout(emitTimer);
  emitTimer = null;
  pendingPatch = null;
  lastState = null;
  seq = 1;
  gmKey = null;
  state = initial();
  writeStored(null);
  notify();
};
